/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReloadOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import R from 'app/assets/R'
import { StatusEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import { BaseSelect } from 'app/components/common/selects/BaseSelect'
import { ExportExcelButton } from 'app/components/header/components/HeaderPage'
import { usePagination } from 'app/hook'
import { IClinic, IUnit } from 'app/model/unit.model'
import { useGetAreaList } from 'app/react-query/hook/location'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { t } from 'i18next'
import { FONT_SIZE, FONT_WEIGHT } from 'parkway-web-common'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { EGroupFeedback, IFormFilter } from '../../type'
import { AdvanceFilterLayout } from './AdvanceFilter'
import { Dayjs } from 'dayjs'
import { RangeValue } from 'app/components/tables/BaseTableManagement/FilterDeptTable/type'
const { RangePicker } = DatePicker
interface IProps {
  onChange?: (value: IFormFilter) => void
  formData: IFormFilter
  handleExportExcel: () => Promise<void>
  isHavePermissionExport: boolean
  isShowGroupFeedback?: boolean
  resetFilter: () => void
}

export const FilterLayout: React.FC<IProps> = ({
  formData,
  onChange,
  isHavePermissionExport,
  handleExportExcel,
  isShowGroupFeedback,
  resetFilter,
}) => {
  const { flattenDataList } = usePagination()

  const { data: areaApi, isLoading: isLoadingArea } = useGetAreaList({
    typeid: 'area',
    status: StatusEnum.ACTIVE,
  })

  const areaList = useMemo(() => {
    const res: IUnit[] = flattenDataList(areaApi)

    let arr: IClinic[] = []
    res?.forEach(i => {
      arr = [...arr, ...(i?.childs || [])]
    })

    return arr?.map(e => ({ ...e, _id: e?._id }))
  }, [areaApi])

  const onChangeClinic = (locationIds: string[]) => {
    onChange && onChange({ ...formData, locationIds })
  }

  const onChangeDate = (data: RangeValue<Dayjs>) => {
    onChange?.({
      ...formData,
      startDateDayJs: data?.[0],
      endDateDayJs: data?.[1],
    })
  }

  const onChangeKeyword = (keyword: string) => {
    onChange && onChange({ ...formData, keyword })
  }

  const onChangeGroupFeedback = (groupFeedback: EGroupFeedback) => {
    onChange && onChange({ ...formData, groupFeedback })
  }

  return (
    <BaseForm>
      <RootWrapper size={0}>
        <BaseRow gutter={[8, 0]} align={'middle'}>
          <BaseCol xl={6}>
            <BaseFormItem hiddenLabel>
              <BaseInput
                placeholder={'Tìm kiếm'}
                value={formData.keyword}
                onChange={e => onChangeKeyword(e?.target?.value)}
              />
            </BaseFormItem>
          </BaseCol>
          {isShowGroupFeedback ? (
            <BaseCol xl={7}>
              <BaseFormItem hiddenLabel>
                <BaseSelectWrapper
                  $prefix="Nhóm feedback"
                  $prefixWidth={120}
                  options={[
                    {
                      label: 'Đã định danh',
                      value: EGroupFeedback.IDENTIFIED,
                    },
                    {
                      label: 'Chưa định danh',
                      value: EGroupFeedback.UNIDENTIFIED,
                    },
                  ]}
                  value={formData?.groupFeedback}
                  onChange={e => onChangeGroupFeedback(e as EGroupFeedback)}
                />
              </BaseFormItem>
            </BaseCol>
          ) : null}

          <BaseCol xl={11}>
            <BaseFormItem hiddenLabel>
              <BaseDatePickerStyled
                format={'DD-MM-YYYY'}
                picker="date"
                onChange={onChangeDate}
                showTime={false}
              />
            </BaseFormItem>
          </BaseCol>
        </BaseRow>
        <BaseRow gutter={[8, 8]} justify={'space-between'} align={'bottom'}>
          <BaseCol xl={18}>
            <BaseRow gutter={[8, 0]} align={'middle'} wrap={false}>
              <BaseCol xl={22}>
                <BaseFormItem hiddenLabel>
                  <BaseSelectWrapper
                    showSearch
                    placeholder={t(R.strings.choose_clinic)}
                    value={formData?.locationIds}
                    allowClear
                    onChange={locations =>
                      onChangeClinic?.(locations as string[])
                    }
                    options={areaList?.map(item => ({
                      value: item._id,
                      label: item.name,
                    }))}
                    loading={isLoadingArea}
                    mode="multiple"
                    maxTagCount={0}
                    $prefix="Phòng khám"
                    $prefixWidth={88}
                  />
                </BaseFormItem>
              </BaseCol>
              <BaseCol>
                <BaseButton
                  icon={<ReloadOutlined rev={undefined} />}
                  onClick={resetFilter}
                />
              </BaseCol>
            </BaseRow>
          </BaseCol>

          <BaseCol xl={6}>
            <BaseRow justify={'end'} align={'middle'} gutter={6} wrap={false}>
              <BaseCol>
                <AdvanceFilterLayout
                  filter={formData}
                  onChangeFilter={onChange}
                />
              </BaseCol>

              {isHavePermissionExport ? (
                <BaseCol>
                  <BaseRow justify={'end'}>
                    <BaseFormItem hiddenLabel>
                      <ExportExcelButton onPressExport={handleExportExcel} />
                    </BaseFormItem>
                  </BaseRow>
                </BaseCol>
              ) : null}
            </BaseRow>
          </BaseCol>
        </BaseRow>
      </RootWrapper>
    </BaseForm>
  )
}

const RootWrapper = styled(BaseSpace)``

const BaseSelectWrapper = styled(BaseSelect)<{
  $prefix?: string
  $prefixWidth?: number
}>`
  width: 100%;
  border: none !important;
  min-width: 300px !important;

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    box-shadow: none !important;
  }
  .ant-select-selector::before {
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.medium};
    color: ${convertedVariables.primaryColor};
    opacity: 0.4;
    align-items: center;
    display: flex;
    content: '${props => props?.$prefix ?? '-'}:';
    width: ${props => props?.$prefixWidth ?? 40}px;
  }

  .ant-select-selection-placeholder {
    padding-left: ${props =>
      props.$prefixWidth ? `${props?.$prefixWidth}px` : 0};
  }
`
const BaseDatePickerStyled = styled(RangePicker)`
  width: 100%;
  border: 1px solid #f0f1f3 !important;

  .ant-picker-input:first-of-type::before {
    content: 'Ngày tiếp nhận feedback:';
    width: 350px;
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.medium};
    color: ${convertedVariables.primaryColor};
    opacity: 0.4;
  }
`

const BaseFormItem = styled(BaseForm.Item)`
  margin-bottom: 0px !important;
`
